import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Book,
  ChartArea,
  CircleQuestionMark,
  Folders,
  History,
  House,
  ListChecks,
  Shapes,
} from 'lucide-react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { ROUTES } from '@/shared/config/routes';
import { usePermissions } from '@/entities/user';
import type { NavigationItem } from '@/shared/types/navigation.types';
import { useTranslation } from 'react-i18next';
import { setSupportSheetOpen, useIsSupportSheetOpen } from '../app-chrome';

type IconKey =
  | 'home'
  | 'sessions'
  | 'projects'
  | 'trackers'
  | 'tools'
  | 'knowledgeHub'
  | 'analytics'
  | 'support';

/**
 * The sidebar rail uses raw Lucide icon components so the consumer can
 * apply per-state classNames (e.g. `text-primary` when active) without
 * having to parse a baked-in className. The `icon` field is kept for
 * back-compat with consumers that render `item.icon` directly; the
 * `IconComponent` field is the new canonical reference for the rail.
 */
const railIconComponents = {
  home: House,
  sessions: History,
  projects: Folders,
  trackers: ListChecks,
  tools: Shapes,
  knowledgeHub: Book,
  analytics: ChartArea,
  support: CircleQuestionMark,
} as const satisfies Record<IconKey, React.ComponentType<{ className?: string }>>;

const railIcons = {
  home: <House className="size-md" />,
  sessions: <History className="size-md" />,
  projects: <Folders className="size-md" />,
  trackers: <ListChecks className="size-md" />,
  tools: <Shapes className="size-md" />,
  knowledgeHub: <Book className="size-md" />,
  analytics: <ChartArea className="size-md" />,
  support: <CircleQuestionMark className="size-md" />,
} as const;

/**
 * Encapsulates the sidebar navigation-item building logic.
 *
 * Consumes feature flags and permissions to determine which items are visible
 * and marks the currently active route.
 */
export function useSidebarNavigation() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccessKnowledgeHub, canAccessSupport, isWorkspaceAdmin } = usePermissions();
  const projectsModuleEnabled = useBooleanFlagValue('projects_module', false);
  const trackersModuleEnabled = useBooleanFlagValue('trackers_module', false);
  const usageAnalyticsEnabled = useBooleanFlagValue('usage_analytics', false);
  const isSupportSheetOpen = useIsSupportSheetOpen();
  const icons = railIcons;
  const iconComponents = railIconComponents;

  const navigationList = useMemo<NavigationItem[]>(() => {
    const items: NavigationItem[] = [
      {
        active: location.pathname === ROUTES.HOME,
        label: t('appShell.useSidebarNavigation.home'),
        id: 'home',
        onClick: () => {
          void navigate(ROUTES.HOME);
        },
        icon: icons.home,
        IconComponent: iconComponents.home,
      },
      {
        active: location.pathname === ROUTES.SESSIONS,
        label: t('appShell.useSidebarNavigation.sessions'),
        id: 'sessions',
        onClick: () => {
          void navigate(ROUTES.SESSIONS);
        },
        icon: icons.sessions,
        IconComponent: iconComponents.sessions,
      },
    ];

    if (trackersModuleEnabled) {
      items.push({
        active: location.pathname.startsWith(ROUTES.TRACKERS),
        label: t('appShell.useSidebarNavigation.trackers'),
        id: 'trackers',
        onClick: () => {
          void navigate(ROUTES.TRACKERS);
        },
        icon: icons.trackers,
        IconComponent: iconComponents.trackers,
      });
    }

    if (projectsModuleEnabled) {
      items.push({
        active: location.pathname.startsWith(ROUTES.PROJECTS),
        label: t('appShell.useSidebarNavigation.projects'),
        id: 'projects',
        onClick: () => {
          void navigate(ROUTES.PROJECTS);
        },
        icon: icons.projects,
        IconComponent: iconComponents.projects,
      });
    }

    items.push({
      active: location.pathname.startsWith(ROUTES.TOOLS_HUB),
      label: t('appShell.useSidebarNavigation.tools'),
      id: 'tools',
      onClick: () => {
        void navigate(ROUTES.TOOLS_HUB);
      },
      icon: icons.tools,
      IconComponent: iconComponents.tools,
    });

    if (canAccessKnowledgeHub) {
      items.push({
        active: location.pathname.startsWith(ROUTES.KNOWLEDGE_HUB),
        label: t('appShell.useSidebarNavigation.knowledge'),
        id: 'knowledge-hub',
        onClick: () => {
          void navigate(ROUTES.KNOWLEDGE_HUB);
        },
        icon: icons.knowledgeHub,
        IconComponent: iconComponents.knowledgeHub,
      });
    }

    // Mirrors the route guard: the page shows workspace-wide usage, so it is
    // admin-only on top of the flag.
    if (usageAnalyticsEnabled && isWorkspaceAdmin) {
      items.push({
        active: location.pathname.startsWith(ROUTES.ANALYTICS),
        label: t('appShell.useSidebarNavigation.analytics'),
        id: 'analytics',
        onClick: () => {
          void navigate(ROUTES.ANALYTICS);
        },
        icon: icons.analytics,
        IconComponent: iconComponents.analytics,
      });
    }

    return items;
  }, [
    location.pathname,
    navigate,
    canAccessKnowledgeHub,
    isWorkspaceAdmin,
    projectsModuleEnabled,
    trackersModuleEnabled,
    usageAnalyticsEnabled,
    icons,
    iconComponents,
    t,
  ]);

  const bottomNavigationList = useMemo<NavigationItem[]>(() => {
    if (!canAccessSupport) return [];

    return [
      {
        active: isSupportSheetOpen,
        onClick: () => setSupportSheetOpen(true),
        label: t('appShell.useSidebarNavigation.support'),
        id: 'support',
        icon: icons.support,
        IconComponent: iconComponents.support,
      },
    ];
  }, [canAccessSupport, isSupportSheetOpen, setSupportSheetOpen, icons, iconComponents, t]);

  return { navigationList, bottomNavigationList };
}
